import {mergeStyles} from 'components/FluentUICompat';
import {EntityPageProps} from 'components';
import {ConfirmDeleteDialog, useDialogState} from 'components/Dialogs';
import {useCommand} from 'hooks';
import React, {useMemo} from 'react';
import {UserRole} from 'types';
import {useEditEntityCmd} from 'hooks/useEditEntityCmd';
import {EntityOfFormPanels} from 'components/EntityPage/EntityFormPanels.types';
import {
	StateForEntityFormPanels,
	useEntityPageTranslation,
	useStateForEntityFormPanels,
} from 'components/EntityPage/hooksForEntityFormPanels';
import {EntityFormPanels} from 'components/EntityPage/EntityFormPanels';
import {useEntityContext} from 'components/EntityPage/EntityContext';
import {FormMode} from 'components/EntityPage/EntityPage.types';

const userRolesInfinity = [
	UserRole.SystemAdministrator,
	UserRole.Vko,
	UserRole.VkoExternal,
	UserRole.Vex,
	UserRole.VexSupport,
	UserRole.Readers,
	UserRole.RegulationReader,
	UserRole.Bav,
	UserRole.ServiceProvider,
	UserRole.ShApprover,
	UserRole.Avk,
];

export function InfinityPage<T extends EntityOfFormPanels>(
	props: EntityPageProps<T>,
) {
	const {t} = useEntityPageTranslation();
	const {
		entityDisplayName,
		deleteEntity,
		onViewItem,
		disableDelete = () => false,
		disableDeleteMany = () => false,
		disableEdit = () => false,
		withNameColumn = true,
		withNameColumnInPanel,
		hideCreate = false,
		hideDelete = false,
		hideDeleteMany = true,
		hideEdit = false,
		onDeleteMany,
		rolesCreate = userRolesInfinity,
		rolesEdit = userRolesInfinity,
		rolesDelete = userRolesInfinity,
		shouldWrapListWithScrollablePane = true,
		propsOfEntityFormPanel,
		className,
		customTranslations,
		...other
	} = props;

	const hideCreateInfinity = hideCreate;
	const hideDeleteInfinity = hideDelete;
	const hideDeleteManyInfinity = hideDeleteMany;
	const hideEditInfinity = hideEdit;
	const rolesCreateInfinity = rolesCreate;
	const rolesEditInfinity = rolesEdit;
	const rolesDeleteInfinity = rolesDelete;

	const stateForPanels: StateForEntityFormPanels<T> =
		useStateForEntityFormPanels();
	const {
		modeInfo: [_mode, setMode],
		formInfo: {reset},
	} = stateForPanels;

	const {selectedItems} = useEntityContext<T>();
	const selectedItemInfinity = selectedItems[0];
	const [deleteDialogState, showDeleteDialog, hideDeleteInfinityDialog] =
		useDialogState();
	const [isDeleteMany, setIsDeleteMany] = React.useState(false);

	const handleDeleteClick = React.useCallback(() => {
		if (isDeleteMany) {
			onDeleteMany?.(selectedItems.map(item => item.id));
		} else if (selectedItemInfinity) {
			deleteEntity(selectedItemInfinity.id);
		}

		hideDeleteInfinityDialog();
	}, [selectedItemInfinity, hideDeleteInfinityDialog, isDeleteMany, onDeleteMany, selectedItems, deleteEntity]);

	const deleteDisabled = useMemo(() => {
		return selectedItems.length > 1
			? selectedItems.length <= 1 || disableDeleteMany?.(selectedItems)
			: !selectedItemInfinity || disableDelete(selectedItemInfinity);
	}, [selectedItemInfinity, selectedItems, disableDelete, disableDeleteMany]);

	useCommand(
		{
			key: 'view',
			text: t('View'),
			iconProps: {iconName: 'View'},
			priority: 1,
			disabled: !selectedItemInfinity,
			hidden: !onViewItem,
			onClick: () => selectedItemInfinity && onViewItem?.(selectedItemInfinity),
		},
		[selectedItemInfinity],
	);
	useCommand(
		{
			key: 'new',
			text: t('New'),
			iconProps: {iconName: 'Add'},
			priority: 2,
			disabled: Boolean(selectedItemInfinity),
			onClick() {
				reset({} as any);
				setMode(FormMode.Create);
			},
			hidden: hideCreateInfinity,
			roles: rolesCreateInfinity,
		},
		[selectedItemInfinity, hideCreateInfinity, rolesCreateInfinity],
	);

	useEditEntityCmd(
		{
			priority: 3,
			disabled: !selectedItemInfinity || disableEdit(selectedItemInfinity),
			key: 'edit',
			onClick() {
				reset(selectedItemInfinity ?? ({} as any));
				// eslint-disable-next-line no-unused-expressions
				(selectedItemInfinity as any).status === 'FINAL'
					? setMode(FormMode.Create)
					: setMode(FormMode.Update);
			},
			hidden: hideEditInfinity,
			roles: rolesEditInfinity,
		},
		[selectedItemInfinity, hideEditInfinity, rolesEditInfinity],
	);

	useCommand(
		{
			key: 'delete',
			text: t('Delete'),
			iconProps: {iconName: 'Delete'},
			priority: 4,
			disabled: deleteDisabled,
			onClick() {
				setIsDeleteMany(selectedItems.length > 1);
				showDeleteDialog();
			},
			hidden:
				selectedItems.length > 1 ? hideDeleteManyInfinity : hideDeleteInfinity,
			roles: rolesDeleteInfinity,
		},
		[
			selectedItems,
			selectedItemInfinity,
			hideDeleteInfinity,
			hideDeleteManyInfinity,
			rolesDeleteInfinity,
			disableDeleteMany,
			onDeleteMany,
		],
	);

	const getClassName = (): string => {
		return mergeStyles([wrapperClassName, className]);
	};

	return (
		<div className={getClassName()}>
			<EntityFormPanels
				entityDisplayName={entityDisplayName}
				entity={selectedItemInfinity}
				performExtraTaskOnCancellation={hideDeleteInfinityDialog}
				withNameColumn={withNameColumnInPanel ?? withNameColumn}
				{...stateForPanels}
				{...other}
				{...propsOfEntityFormPanel}
			/>
			<ConfirmDeleteDialog
				{...deleteDialogState}
				onConfirm={handleDeleteClick}
				itemCount={isDeleteMany ? selectedItems.length : undefined}
				customSubtextTranslationKey={
					isDeleteMany
						? customTranslations?.deleteManySubtextTranslationKey
						: customTranslations?.deleteSubtextTranslationKey
				}
			/>
		</div>
	);
}

const wrapperClassName = mergeStyles({
	height: '100%',
	position: 'relative',
});
